/**
 * League Service - Handles league-related API calls
 */

import { ApiClient, SERVICE_URLS, gamificationServiceApi } from '@/services/api';

const gamificationServiceApiV2 = new ApiClient(() =>
  SERVICE_URLS.GAMIFICATION_SERVICE.replace('/api/v1/gamification-service', '/api/v2/gamification-service')
);

// Types for League API responses
export interface LeagueParticipant {
  participant_id: number;
  user_id: number;
  avatar_url: string | null;
  name: string;
  points_in_cycle: number;
  rank: number;
  is_current_user: boolean;
}

export interface League {
  league_id: number;
  name: string;
  rank_level: number;
  promotion_rank: number;
  demotion_rank: number;
  duration_days: number;
  top_3_bonus: number;
  promotion_bonus: number;
  stay_bonus: number;
  icon: string | null;
  created_at: string | null;
}

export interface LeagueLeaderboard {
  cycle_id: number;
  league_name: string;
  league_id: number;
  rank_level: number;
  promotion_rank: number;
  demotion_rank: number;
  start_date: string;
  end_date: string;
  remaining_time_hours: number;
  total_participants: number;
  current_user_rank: number;
  participants: LeagueParticipant[];
}

export interface LeaguePreview {
  state: 'active' | 'needs_activity';
  target_league_id: number;
  target_league_name: string;
  message: string | null;
  leaderboard: LeagueLeaderboard | null;
}

/**
 * Get current league leaderboard
 * @returns Promise with current league data and participants
 */
export const getCurrentLeaderboard = async (): Promise<LeaguePreview> => {
  console.log('🏆 [LeagueService] Fetching league preview...');

  const response = await gamificationServiceApiV2.get<any>('/leagues/current-leaderboard');

  if (response.success && response.data) {
    // ApiClient returns the whole JSON body as data. Backend uses { success, data, message }.
    // Unwrap to the actual leaderboard payload if nested under data.
    const raw = response.data as any;
    const preview: LeaguePreview = raw?.data ?? raw;
    console.log('✅ [LeagueService] League information fetched successfully');
    return preview;
  }

  const message = response.error || 'خطا در دریافت اطلاعات لیگ';
  console.error('❌ [LeagueService] Failed to fetch leaderboard:', message);
  throw new Error(message);
};

/**
 * Format remaining time for display
 * @param hours - Remaining hours
 * @returns Formatted time string in Persian
 */
export const formatRemainingTime = (hours: number): string => {
  const h = Number(hours);
  if (!isFinite(h) || isNaN(h) || h < 0) {
    return 'نامشخص';
  }

  if (h < 24) {
    return `${Math.floor(h)} ساعت`;
  }

  const days = Math.floor(h / 24);
  const remainingHours = Math.floor(h % 24);

  if (remainingHours === 0) {
    return `${days} روز`;
  }

  return `${days} روز و ${remainingHours} ساعت`;
};

/**
 * Get league icon based on league name
 * @param leagueName - Name of the league
 * @returns Trophy emoji for the league
 */
export const getLeagueIcon = (leagueName: string): string => {
  const name = leagueName.toLowerCase();

  if (name.includes('gold') || name.includes('طلا')) {
    return '🏆'; // Gold trophy
  } else if (name.includes('silver') || name.includes('نقره')) {
    return '🥈'; // Silver medal
  } else if (name.includes('bronze') || name.includes('برنز')) {
    return '🥉'; // Bronze medal
  } else if (name.includes('diamond') || name.includes('الماس')) {
    return '💎'; // Diamond
  } else if (name.includes('platinum') || name.includes('پلاتین')) {
    return '🏅'; // Platinum medal
  }

  return '🏆'; // Default gold trophy
};

/**
 * Get all leagues (for header badges/icons)
 * Endpoint: GET /api/leagues
 */
export const getLeagues = async (): Promise<League[]> => {
  console.log('📚 [LeagueService] Fetching leagues list...');
  const response = await gamificationServiceApi.get<any>('/leagues');
  if (response.success && response.data) {
    const raw = response.data as any;
    // Backend returns { success, message, data: { leagues, total } }
    const payload = raw?.data ?? raw;
    const leagues: League[] = payload?.leagues ?? [];
    return leagues;
  }
  const message = response.error || 'خطا در دریافت لیست لیگ‌ها';
  throw new Error(message);
};
